
import Component from "vue-class-component";
import Vue from "vue";
import {
  PrintBackgroundTemplateModel,
  SysAdminPrintBackgroundTemplatesClient,
  SysAdminPrintBackgroundTemplatesSortField,
} from "@/api-client";
import { BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import ServersidePaging from "@/components/ServersidePaging";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
import Loader from "@/features/shared/components/Loader";
import { debounceFn } from "debounce-decorator-ts";
import { formatDate } from "@/formatting/dateFormatter";

const nameString = "name";
const createdAtString = "createdAt";

const defaultSortValue =
  SysAdminPrintBackgroundTemplatesSortField.CreatedAtDesc;

class Sort {
  value: SysAdminPrintBackgroundTemplatesSortField | null = null;

  constructor(
    value: SysAdminPrintBackgroundTemplatesSortField | null = defaultSortValue
  ) {
    this.value = value;
  }

  get fieldName(): string | null {
    switch (this.value) {
      case SysAdminPrintBackgroundTemplatesSortField.NameAsc:
      case SysAdminPrintBackgroundTemplatesSortField.NameDesc:
        return nameString;
      case SysAdminPrintBackgroundTemplatesSortField.CreatedAtAsc:
      case SysAdminPrintBackgroundTemplatesSortField.CreatedAtDesc:
        return createdAtString;
      default:
        return null;
    }
  }

  get isSortedByDesc(): boolean {
    return (
      this.value == null ||
      this.value == SysAdminPrintBackgroundTemplatesSortField.NameDesc ||
      this.value == SysAdminPrintBackgroundTemplatesSortField.CreatedAtDesc
    );
  }
}

@Component({
  components: {
    TableEmptySlot,
    HeadingWave,
    SectionPageHeading,
    ServersidePagingSelector,
  },
})
export default class PrintBackgroundTemplates extends Vue {
  templates: PrintBackgroundTemplateModel[] = [];

  paging = new ServersidePaging();
  loader = new Loader(true);
  sort = new Sort();

  searchPhrase = "";

  created() {
    this.getParametersFromRouteOrDefault();

    this.$watch(
      () => this.searchPhrase,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.paging.pageNumber,
      () => {
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.paging.pageSize,
      () => {
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.sort,
      () => {
        this.reflectParametersToRoute();
      },
      { deep: true }
    );

    this.$watch(
      () => this.$route.query,
      () => {
        this.search();
      },
      { deep: true, immediate: true }
    );
  }

  @debounceFn(100) // Prevents possible double-update
  reflectParametersToRoute(): void {
    this.$router.replace({
      query: {
        // Setting to 'undefined' removes parameter from URL.
        searchPhrase: this.searchPhrase || undefined,
        pageNumber: this.paging.pageNumber
          ? this.paging.pageNumber.toString()
          : undefined,
        pageSize: this.paging.pageSize
          ? this.paging.pageSize.toString()
          : undefined,
        sort: this.sort.value || undefined,
      },
    });
  }

  getParametersFromRouteOrDefault() {
    const route = this.$route as any;
    const query = route.query;
    this.searchPhrase = query.searchPhrase ? query.searchPhrase : "";
    this.paging.pageNumber =
      parseInt(route.query.pageNumber) || this.paging.pageNumber;
    this.paging.pageSize =
      parseInt(route.query.pageSize) || this.paging.pageSize;
    this.sort = new Sort(route.query.sort || defaultSortValue);
  }

  updateSort(ctx: BvTableCtxObject) {
    switch (ctx.sortBy) {
      case nameString:
        this.sort = new Sort(
          ctx.sortDesc
            ? SysAdminPrintBackgroundTemplatesSortField.NameDesc
            : SysAdminPrintBackgroundTemplatesSortField.NameAsc
        );
        break;
      case createdAtString:
        this.sort = new Sort(
          ctx.sortDesc
            ? SysAdminPrintBackgroundTemplatesSortField.CreatedAtDesc
            : SysAdminPrintBackgroundTemplatesSortField.CreatedAtAsc
        );
        break;
      default:
        this.sort = new Sort();
        break;
    }
  }

  async search() {
    this.templates = [];

    new SysAdminPrintBackgroundTemplatesClient()
      .setLoader(this.loader)
      .search(
        this.sort.value,
        this.paging.pageNumber,
        this.paging.pageSize,
        this.searchPhrase
      )
      .then((queryResult) => {
        this.templates = queryResult.pageResult;
        this.paging.totalCount = queryResult.totalCount;
        this.paging.pageSize = queryResult.pageSize;
      });
  }

  updatePrintBackground(update: PrintBackgroundTemplateModel) {
    this.$router.push({
      name: "edit-print-background-template",
      params: { id: update.id! },
    });
  }

  fields: BvTableFieldArray = [
    {
      key: nameString,
      label: this.$ls.Global_Label_Name,
      sortable: true,
      sortByFormatted: true,
      thClass: "w-50",
    },
    {
      key: "id",
      label: this.$ls.Global_Label_Id,
      sortable: false,
    },
    {
      key: createdAtString,
      label: this.$ls.Global_Label_CreatedAtShort,
      sortable: true,
      sortByFormatted: true,
      formatter: (
        value: any,
        key: any,
        item: PrintBackgroundTemplateModel
      ): string => formatDate(item.createdAt),
    },
    {
      key: "linkIcon",
      label: "",
      sortable: false,
      tdClass: "table-actions",
    },
  ];
}
